import { useState } from 'react'
import { buildFormDefaultValues } from '../../helpers/buildFormDefaultValues'
import { isFormValid } from '../../helpers/isFormValid'
import { serializeFieldValue } from '../../helpers/serializeFieldValue'
import { validateCron } from '../../helpers/validateCron'
import { SearchableSelect } from '../SearchableSelect/SearchableSelect'
import type { CreateEntityFormProps } from './CreateEntityForm.types'
import './CreateEntityForm.css'
import { sortSelectOptions } from '../../helpers/sortSelectOptions'

export function CreateEntityForm({
  fields,
  onSubmit,
  submitLabel = 'Create',
  isLoading = false,
  extraRow,
  isExtraRowValid = true,
}: CreateEntityFormProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    buildFormDefaultValues(fields)
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [touched, setTouched] = useState<Set<string>>(new Set())

  const formIsValid = isFormValid(fields, values) && isExtraRowValid

  function setValue(name: string, value: unknown) {
    const field = fields.find((f) => f.name === name)
    const derived = field?.deriveValues?.(value) ?? {}
    setValues((prev) => ({ ...prev, [name]: value, ...derived }))
  }

  function markTouched(name: string) {
    setTouched((prev) => new Set(prev).add(name))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!formIsValid || isSubmitting || isLoading) return

    setIsSubmitting(true)
    try {
      const body = fields.reduce<Record<string, unknown>>(
        (acc, field) => ({
          ...acc,
          [field.name]: serializeFieldValue(field, values[field.name]),
        }),
        {}
      )
      await onSubmit(body)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={isLoading}>
        <div className="create-form-table">
          {fields.map((field, index) => {
            const isLast = index === fields.length - 1 && !extraRow
            const cronError =
              field.type === 'cron' && touched.has(field.name)
                ? validateCron(String(values[field.name] ?? ''))
                : null
            const customError =
              touched.has(field.name) && field.validate
                ? field.validate(values[field.name])
                : null
            const validationError = cronError ?? customError

            return (
              <div
                key={field.name}
                className={isLast ? 'create-form-row--last' : 'create-form-row'}
              >
                <div className="create-form-label">
                  {field.label}
                  {field.type !== 'checkbox' && field.required !== false && (
                    <span className="create-form-required" aria-hidden="true">
                      *
                    </span>
                  )}
                </div>
                <div className="create-form-input-cell">
                  {field.type === 'text' && (
                    <div className="w-100">
                      <input
                        type="text"
                        className={`form-control form-control-sm${validationError ? ' is-invalid' : ''}`}
                        value={String(values[field.name] ?? '')}
                        onChange={(e) => setValue(field.name, e.target.value)}
                        onBlur={() => markTouched(field.name)}
                        required={field.required !== false}
                        disabled={field.isDisabled}
                        aria-label={field.label}
                      />
                      {validationError && (
                        <div className="invalid-feedback d-block">
                          {validationError}
                        </div>
                      )}
                    </div>
                  )}
                  {field.type === 'cron' && (
                    <div className="w-100">
                      <input
                        type="text"
                        className={`form-control form-control-sm font-monospace${validationError ? ' is-invalid' : ''}`}
                        value={String(values[field.name] ?? '')}
                        placeholder="* * * * *"
                        onChange={(e) => setValue(field.name, e.target.value)}
                        onBlur={() => markTouched(field.name)}
                        required={field.required !== false}
                      />
                      {validationError && (
                        <div className="invalid-feedback d-block">
                          {validationError}
                        </div>
                      )}
                    </div>
                  )}
                  {field.type === 'number' && (
                    <input
                      type="number"
                      step="any"
                      className="form-control form-control-sm"
                      value={String(values[field.name] ?? '')}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      onBlur={() => markTouched(field.name)}
                      required={field.required !== false}
                    />
                  )}
                  {field.type === 'checkbox' && (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={field.name}
                      checked={Boolean(values[field.name])}
                      onChange={(e) => setValue(field.name, e.target.checked)}
                    />
                  )}
                  {field.type === 'select' && field.searchable && (
                    <SearchableSelect
                      value={String(values[field.name] ?? '')}
                      options={field.options}
                      ariaLabel={field.label}
                      isRequired={field.required !== false}
                      onChange={(value) => setValue(field.name, value)}
                      onBlur={() => markTouched(field.name)}
                    />
                  )}
                  {field.type === 'select' && !field.searchable && (
                    <select
                      className="form-select form-select-sm"
                      value={String(values[field.name] ?? '')}
                      onChange={(e) => setValue(field.name, e.target.value)}
                      onBlur={() => markTouched(field.name)}
                      required={field.required !== false}
                    >
                      <option value="">Select…</option>
                      {sortSelectOptions(field.options).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )
          })}

          {extraRow && (
            <div className="create-form-row--last">
              <div className="create-form-label">
                {extraRow.label}
                {extraRow.isRequired && (
                  <span className="create-form-required" aria-hidden="true">
                    *
                  </span>
                )}
              </div>
              <div className="create-form-input-cell">{extraRow.content}</div>
            </div>
          )}
        </div>
      </fieldset>

      <div className="d-flex justify-content-end">
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={!formIsValid || isSubmitting || isLoading}
        >
          {isSubmitting && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
          )}
          {submitLabel}
        </button>
      </div>
    </form>
  )
}
